import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useDesignStore from '../../store/designStore';
import useProjectStore from '../../store/projectStore';
import useAuthStore from '../../store/authStore';
import useUIStore from '../../store/uiStore';
import ScrollX from '../../components/ScrollX';

const initialForm = {
  project_id: '',
  jacket_type: 'annular',
  mode: 'heating',
  vessel_inside_diameter_mm: '',
  vessel_shell_thickness_mm: '',
  vessel_straight_shell_height_mm: '',
  shell_coverage_percent: '50',
  bottom_head_coverage_percent: '50',
  vessel_operating_pressure_bar: '0',
  batch_volume_liters: '',
  batch_fluid_density_kgm3: '997',
  batch_fluid_specific_heat_kj_kgk: '4.186',
  batch_initial_temp_c: '',
  batch_target_temp_c: '',
  steam_pressure_barg: '4',
  chilled_water_supply_temp_c: '7',
  chilled_water_return_temp_c: '12',
  overall_heat_transfer_coefficient_w_m2k: '',
  jacket_design_pressure_bar: '',
  design_temperature_c: '150',
  material: 'SA-240 316L',
  allowable_stress_mpa: '',
  joint_efficiency: '0.85',
  corrosion_allowance_mm: '1.5',
  annular_gap_mm: '50',
  half_pipe_outside_diameter_mm: '88.9',
  half_pipe_wall_thickness_mm: '5.49',
  coil_pitch_mm: '150',
  assume_stiffening_effect: false,
  ring_spacing_mm: '',
};

const JacketDesignCalculatorPage = () => {
  const [form, setForm] = useState(initialForm);
  const [showDetails, setShowDetails] = useState(true);
  const { projects, fetchProjects } = useProjectStore();
  const { user } = useAuthStore();
  const { result, isCalculating, isSubmitting, error, calculate, saveCalculation, clearResult } = useDesignStore();
  const showToast = useUIStore((s) => s.showToast);
  const navigate = useNavigate();

  useEffect(() => {
    fetchProjects();
    clearResult();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (error) showToast(error, 'error');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const handleChange = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
  };

  const buildInputs = () => {
    if (!form.vessel_inside_diameter_mm || !form.vessel_shell_thickness_mm || !form.vessel_straight_shell_height_mm) {
      showToast('Vessel diameter, shell thickness, and shell height are required', 'error');
      return null;
    }
    if (!form.batch_volume_liters || !form.batch_initial_temp_c || !form.batch_target_temp_c) {
      showToast('Batch volume, initial temperature, and target temperature are required', 'error');
      return null;
    }
    if (!form.jacket_design_pressure_bar) {
      showToast('Jacket design pressure is required', 'error');
      return null;
    }
    return {
      jacket_type: form.jacket_type,
      mode: form.mode,
      vessel_inside_diameter_mm: Number(form.vessel_inside_diameter_mm),
      vessel_shell_thickness_mm: Number(form.vessel_shell_thickness_mm),
      vessel_straight_shell_height_mm: Number(form.vessel_straight_shell_height_mm),
      shell_coverage_percent: Number(form.shell_coverage_percent || 50),
      bottom_head_coverage_percent: Number(form.bottom_head_coverage_percent || 50),
      vessel_operating_pressure_bar: Number(form.vessel_operating_pressure_bar || 0),
      batch_volume_liters: Number(form.batch_volume_liters),
      batch_fluid_density_kgm3: Number(form.batch_fluid_density_kgm3),
      batch_fluid_specific_heat_kj_kgk: Number(form.batch_fluid_specific_heat_kj_kgk),
      batch_initial_temp_c: Number(form.batch_initial_temp_c),
      batch_target_temp_c: Number(form.batch_target_temp_c),
      steam_pressure_barg: Number(form.steam_pressure_barg || 4),
      chilled_water_supply_temp_c: Number(form.chilled_water_supply_temp_c || 7),
      chilled_water_return_temp_c: Number(form.chilled_water_return_temp_c || 12),
      overall_heat_transfer_coefficient_w_m2k: form.overall_heat_transfer_coefficient_w_m2k === '' ? null : Number(form.overall_heat_transfer_coefficient_w_m2k),
      jacket_design_pressure_bar: Number(form.jacket_design_pressure_bar),
      design_temperature_c: Number(form.design_temperature_c || 150),
      material: form.material,
      allowable_stress_mpa: form.allowable_stress_mpa === '' ? null : Number(form.allowable_stress_mpa),
      joint_efficiency: Number(form.joint_efficiency),
      corrosion_allowance_mm: Number(form.corrosion_allowance_mm),
      annular_gap_mm: Number(form.annular_gap_mm || 50),
      half_pipe_outside_diameter_mm: Number(form.half_pipe_outside_diameter_mm || 88.9),
      half_pipe_wall_thickness_mm: Number(form.half_pipe_wall_thickness_mm || 5.49),
      coil_pitch_mm: Number(form.coil_pitch_mm || 150),
      assume_stiffening_effect: form.assume_stiffening_effect,
      ring_spacing_mm: form.ring_spacing_mm === '' ? null : Number(form.ring_spacing_mm),
    };
  };

  const handleCalculate = async (e) => {
    e.preventDefault();
    const inputs = buildInputs();
    if (!inputs) return;
    await calculate('jacket_design', inputs);
  };

  const handleSave = async () => {
    if (!result) return;
    const payload = {
      calculator_type: 'jacket_design',
      project_id: form.project_id ? Number(form.project_id) : null,
      created_by_id: user?.id || null,
      client_data: result.inputs,
      calculation_outputs: result.outputs,
    };
    const saveResult = await saveCalculation(payload);
    if (saveResult.success) {
      showToast('Calculation saved as draft', 'success');
      navigate('/design/calculations');
    } else {
      showToast(saveResult.error, 'error');
    }
  };

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-xl font-bold text-white mb-1">Jacket / Limpet Coil Design</h1>
        <p className="text-xs text-gray-500 mb-6">
          Batch heating/cooling time + jacket wall thickness for an agitated jacketed vessel.
          Half-pipe structural sizing is a simplified standalone-pipe check, not the full ASME
          Appendix EE method. See "Assumptions" below the result. Requires review by a qualified
          pressure vessel engineer before fabrication.
        </p>

        <div className="glass-card p-6">
          <form onSubmit={handleCalculate} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Project (optional)</label>
              <select
                value={form.project_id}
                onChange={handleChange('project_id')}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              >
                <option value="">Not linked to a project</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Jacket Type *</label>
                <div className="flex flex-col gap-2 text-sm">
                  <label className="flex items-center gap-2">
                    <input type="radio" checked={form.jacket_type === 'annular'} onChange={() => setForm((f) => ({ ...f, jacket_type: 'annular' }))} />
                    Annular (conventional)
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="radio" checked={form.jacket_type === 'half_pipe'} onChange={() => setForm((f) => ({ ...f, jacket_type: 'half_pipe' }))} />
                    Half-Pipe (Limpet Coil)
                  </label>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Duty *</label>
                <div className="flex flex-col gap-2 text-sm">
                  <label className="flex items-center gap-2">
                    <input type="radio" checked={form.mode === 'heating'} onChange={() => setForm((f) => ({ ...f, mode: 'heating' }))} />
                    Heating (steam)
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="radio" checked={form.mode === 'cooling'} onChange={() => setForm((f) => ({ ...f, mode: 'cooling' }))} />
                    Cooling (chilled water)
                  </label>
                </div>
              </div>
            </div>

            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 pt-2">Vessel Geometry</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Inside Diameter (mm) *</label>
                <input required type="number" step="any" value={form.vessel_inside_diameter_mm} onChange={handleChange('vessel_inside_diameter_mm')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Shell Thickness (mm) *</label>
                <input required type="number" step="any" value={form.vessel_shell_thickness_mm} onChange={handleChange('vessel_shell_thickness_mm')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Straight Shell Height (mm) *</label>
                <input required type="number" step="any" value={form.vessel_straight_shell_height_mm} onChange={handleChange('vessel_straight_shell_height_mm')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Shell Coverage (%)</label>
                <input type="number" step="any" value={form.shell_coverage_percent} onChange={handleChange('shell_coverage_percent')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" placeholder="Default: 50" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bottom Dish Coverage (%)</label>
                <input type="number" step="any" value={form.bottom_head_coverage_percent} onChange={handleChange('bottom_head_coverage_percent')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" placeholder="Default: 50" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Vessel Operating Pressure (bar)</label>
                <input type="number" step="any" value={form.vessel_operating_pressure_bar} onChange={handleChange('vessel_operating_pressure_bar')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
              </div>
            </div>

            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 pt-2">Batch</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Batch Volume (L) *</label>
                <input required type="number" step="any" value={form.batch_volume_liters} onChange={handleChange('batch_volume_liters')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Initial Temp (°C) *</label>
                <input required type="number" step="any" value={form.batch_initial_temp_c} onChange={handleChange('batch_initial_temp_c')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Target Temp (°C) *</label>
                <input required type="number" step="any" value={form.batch_target_temp_c} onChange={handleChange('batch_target_temp_c')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
              </div>
            </div>

            {form.mode === 'heating' ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Steam Supply Pressure (barg)</label>
                <input type="number" step="any" value={form.steam_pressure_barg} onChange={handleChange('steam_pressure_barg')} className="w-48 border border-gray-300 rounded px-3 py-2 text-sm" />
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Chilled Water Supply (°C)</label>
                  <input type="number" step="any" value={form.chilled_water_supply_temp_c} onChange={handleChange('chilled_water_supply_temp_c')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Chilled Water Return (°C)</label>
                  <input type="number" step="any" value={form.chilled_water_return_temp_c} onChange={handleChange('chilled_water_return_temp_c')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Overall U (W/m²K)</label>
              <input type="number" step="any" value={form.overall_heat_transfer_coefficient_w_m2k} onChange={handleChange('overall_heat_transfer_coefficient_w_m2k')} className="w-48 border border-gray-300 rounded px-3 py-2 text-sm" placeholder="Blank = typical published default" />
            </div>

            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 pt-2">Structural</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Jacket Design Pressure (bar) *</label>
                <input required type="number" step="any" value={form.jacket_design_pressure_bar} onChange={handleChange('jacket_design_pressure_bar')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Design Temperature (°C)</label>
                <input type="number" step="any" value={form.design_temperature_c} onChange={handleChange('design_temperature_c')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Allowable Stress, S (MPa)</label>
                <input type="number" step="any" value={form.allowable_stress_mpa} onChange={handleChange('allowable_stress_mpa')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" placeholder="Blank = typical default" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Joint Efficiency, E</label>
                <input type="number" step="any" max="1" value={form.joint_efficiency} onChange={handleChange('joint_efficiency')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Corrosion Allowance (mm)</label>
                <input type="number" step="any" value={form.corrosion_allowance_mm} onChange={handleChange('corrosion_allowance_mm')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Material</label>
                <input type="text" value={form.material} onChange={handleChange('material')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
              </div>
            </div>

            {form.jacket_type === 'annular' ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Annular Gap (mm)</label>
                <input type="number" step="any" value={form.annular_gap_mm} onChange={handleChange('annular_gap_mm')} className="w-48 border border-gray-300 rounded px-3 py-2 text-sm" />
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Half-Pipe OD (mm)</label>
                  <input type="number" step="any" value={form.half_pipe_outside_diameter_mm} onChange={handleChange('half_pipe_outside_diameter_mm')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" placeholder="Default: NPS 3 Sch 40" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Half-Pipe Wall Thickness (mm)</label>
                  <input type="number" step="any" value={form.half_pipe_wall_thickness_mm} onChange={handleChange('half_pipe_wall_thickness_mm')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Coil Pitch (mm)</label>
                  <input type="number" step="any" value={form.coil_pitch_mm} onChange={handleChange('coil_pitch_mm')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" placeholder="Turn-to-turn spacing" />
                </div>
              </div>
            )}

            <div className="border-t border-gray-100 pt-4">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                <input
                  type="checkbox"
                  checked={form.assume_stiffening_effect}
                  onChange={(e) => setForm((f) => ({ ...f, assume_stiffening_effect: e.target.checked }))}
                />
                Show indicative stiffening-ring credit (jacket pressure vs. inner wall)
              </label>
              <p className="text-xs text-gray-400 mb-3">
                Unvalidated directional estimate only — shown alongside, never replacing, the conservative
                requirement above. See warnings on the result.
              </p>

              {form.assume_stiffening_effect && form.jacket_type === 'annular' && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Assumed Stiffening Ring Spacing (mm)</label>
                  <input type="number" step="any" value={form.ring_spacing_mm} onChange={handleChange('ring_spacing_mm')} className="w-48 border border-gray-300 rounded px-3 py-2 text-sm" placeholder="Blank = 300mm default" />
                </div>
              )}
              {form.assume_stiffening_effect && form.jacket_type === 'half_pipe' && (
                <p className="text-xs text-gray-500">Uses the Coil Pitch entered above as the stiffener spacing.</p>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button type="submit" disabled={isCalculating} className="px-4 py-2 text-sm rounded bg-komal-blue hover:bg-komal-blue-dark text-white font-semibold disabled:opacity-60">
                {isCalculating ? 'Calculating...' : 'Calculate'}
              </button>
            </div>
          </form>
        </div>

        {result && (
          <div className="glass-card p-6 mt-4">
            <h2 className="font-semibold text-gray-900 mb-4">Result</h2>

            {result.outputs.warnings.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded p-3 mb-4 space-y-1">
                {result.outputs.warnings.map((w, i) => (
                  <p key={i} className="text-xs text-amber-800">⚠ {w}</p>
                ))}
              </div>
            )}

            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Heat Transfer</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm mb-4">
              <Stat label="Total Jacket Area" value={`${result.outputs.total_jacket_area_m2} m²`} />
              <Stat label="Covered Shell Height" value={`${result.outputs.covered_shell_height_mm} mm`} />
              <Stat label="Batch Mass" value={`${result.outputs.batch_mass_kg} kg`} />
              <Stat label="Overall U" value={`${result.outputs.overall_u_w_m2k} W/m²K${result.outputs.u_value_is_default ? ' (default)' : ''}`} />
              <Stat label="Batch Time" value={`${result.outputs.time_hours} hr`} highlight />
              <Stat label="Initial (Peak) Duty" value={`${result.outputs.duty_initial_kw} kW`} />
              <Stat label="Final Duty" value={`${result.outputs.duty_final_kw} kW`} />
              {result.outputs.utility_type === 'steam' ? (
                <Stat label="Steam Required" value={`${result.outputs.utility_flow_kg_hr} kg/hr`} highlight />
              ) : (
                <Stat label="Chilled Water Required" value={`${result.outputs.utility_flow_m3_hr} m³/hr`} highlight />
              )}
            </div>

            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Structural</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm mb-4">
              <Stat label="Allowable Stress (S)" value={`${result.outputs.allowable_stress_mpa} MPa${result.outputs.s_value_is_default ? ' (default)' : ''}`} />
              {result.outputs.jacket_wall_thickness_required_mm != null ? (
                <>
                  <Stat label="Jacket Wall (required)" value={`${result.outputs.jacket_wall_thickness_required_mm} mm`} />
                  <Stat label="Jacket Recommended Plate" value={`${result.outputs.jacket_wall_recommended_plate_mm} mm`} highlight />
                </>
              ) : (
                <>
                  <Stat label="Half-Pipe Wall (required)" value={`${result.outputs.half_pipe_wall_thickness_required_mm} mm`} />
                  <Stat label="Given Wall Adequate?" value={result.outputs.half_pipe_given_wall_adequate ? 'Yes' : 'No — increase schedule'} highlight />
                  <Stat label="Coil Length" value={`${result.outputs.coil_length_m} m (${result.outputs.coil_number_of_turns} turns)`} highlight />
                </>
              )}
              {result.outputs.inner_wall_check && (
                <Stat
                  label="Inner Wall vs Jacket Pressure"
                  value={result.outputs.inner_wall_check.inner_wall_given_adequate ? 'Adequate' : `Insufficient (need ${result.outputs.inner_wall_check.inner_wall_thickness_required_mm}mm)`}
                  highlight={!result.outputs.inner_wall_check.inner_wall_given_adequate}
                />
              )}
              {result.outputs.inner_wall_check?.stiffening_credit && (
                <Stat
                  label="Indicative Stiffened Thickness"
                  value={`~${result.outputs.inner_wall_check.stiffening_credit.indicative_stiffened_thickness_required_mm} mm (unverified)`}
                />
              )}
            </div>

            {(result.outputs.calculation_steps || result.outputs.assumptions) && (
              <div className="mt-6 pt-4 border-t border-gray-100">
                <button type="button" onClick={() => setShowDetails((v) => !v)} className="text-xs font-semibold text-komal-blue hover:underline mb-3">
                  {showDetails ? '− Hide calculation & assumptions' : '+ Show calculation & assumptions'}
                </button>

                {showDetails && (
                  <div className="space-y-4">
                    {result.outputs.calculation_steps?.length > 0 && (
                      <div>
                        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Calculation Steps</h3>
                        <div className="bg-gray-50 rounded border border-gray-100">
                          <ScrollX>
                          <table className="w-full text-xs">
                            <tbody>
                              {result.outputs.calculation_steps.map((step, i) => (
                                <tr key={i} className="border-b border-gray-100 last:border-0">
                                  <td className="px-3 py-2 font-medium text-gray-700 whitespace-nowrap">{step.step}</td>
                                  <td className="px-3 py-2 text-gray-500 font-mono whitespace-nowrap">{step.formula}</td>
                                  <td className="px-3 py-2 text-gray-900 font-mono">{step.result}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          </ScrollX>
                        </div>
                      </div>
                    )}

                    {result.outputs.assumptions?.length > 0 && (
                      <div>
                        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Assumptions</h3>
                        <ul className="list-disc list-inside space-y-1">
                          {result.outputs.assumptions.map((a, i) => (
                            <li key={i} className="text-xs text-gray-600">{a}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end gap-3 pt-6">
              <button type="button" onClick={() => clearResult()} className="px-4 py-2 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50">
                Discard
              </button>
              <button type="button" onClick={handleSave} disabled={isSubmitting} className="px-4 py-2 text-sm rounded bg-green-600 hover:bg-green-700 text-white font-semibold disabled:opacity-60">
                {isSubmitting ? 'Saving...' : 'Save as Draft'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Stat = ({ label, value, highlight }) => (
  <div>
    <div className="text-xs text-gray-500">{label}</div>
    <div className={`font-semibold ${highlight ? 'text-komal-blue text-lg' : 'text-gray-900'}`}>{value}</div>
  </div>
);

export default JacketDesignCalculatorPage;
